/**
 * Widths the topbar's slots agree on. `Topbar` renders against them and
 * `TopbarActions` measures against them, so they live in one module — a centre
 * slot the measurement does not know about lets the trailing actions overlap it.
 */

// Room kept free for the topbar's leading slot (breadcrumb, owner names, status
// and phase pills). Only the name truncates — the pills do not shrink — so once
// the actions eat into this the breadcrumb is already unreadable.
export const MIN_LEADING_WIDTH = 360;

// The centre slot's width budget (the global search entry). Matches the
// finalised design's 520px field. It is a cap, not a demand — the slot shrinks.
export const CENTRE_MAX_WIDTH = 520;

// The centre slot's cap on a page that asks for `PageTopbar compactCentre`: a
// detail header that carries more adornments than the 360px leading floor can
// hold (the Objekt header with its status pill and „Reserviert“ badge). The
// leading slot is `flex-1`, so every pixel the search entry gives up here is
// the record title's — the search yields, the title does not (IMM-1145).
export const CENTRE_COMPACT_MAX_WIDTH = 190;

// …and its floor on the same pages. At the 240px cap the Objekt title kept only
// ~137px at 1280 (UX review of IMM-1145); 190px hands it ~187px, above the 180px
// the design handoff asks for. The search entry truncates its own placeholder, a
// record title is what the page is about.
export const CENTRE_COMPACT_MIN_WIDTH = 140;

// What the centre slot is guaranteed, and therefore all the trailing actions
// have to give up for it. Reserving the full 520px cap instead would push the
// collapse threshold 536px out and drop the primary page action into the kebab
// on ordinary laptop widths, where today it renders inline — for space the
// search entry does not insist on.
export const CENTRE_MIN_WIDTH = 200;

// The `xl:gap-4` the topbar puts between adjacent slots at and above the
// collapse breakpoint. Below `xl` the header uses `gap-2` instead — this
// constant is a measurement input for `topbar-actions.tsx`, which has no
// production call sites today, so nothing renders it.
export const SLOT_GAP = 16;

/** The names under which `Topbar` renders the widths above onto the `<header>`. */
export const TOPBAR_WIDTH_VARS = {
  leadingMin: '--topbar-leading-min',
  centreMin: '--topbar-centre-min',
  centreMax: '--topbar-centre-max',
} as const;

// Tailwind's `xl`. Below it the centre slot collapses to the search icon.
export const TOPBAR_CENTRE_BREAKPOINT = 1280;
